import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useWeb3 } from '../context/Web3Context';
import { Web3 } from 'web3';
import Header from '../components/Header';
import Footer from '../components/Footer';
import UserChat from '../components/UserChat';
import SpotsTab from '../components/SpotsTab';
import PhoneAIChat from '../components/PhoneAIChat';
import UserAnalyticsOverview from '../components/UserAnalyticsOverview';
import ServiceOrdersModal from '../components/ServiceOrdersModal';
import AdminShell from '../components/AdminShell';
import Card from '../components/primitives/Card';
import api from '../utils/api';
import { toast } from 'react-toastify';
import { NFT_CONTRACT_ADDRESS, NFT_ABI } from '../config/contracts';
import '../styles/theme.css';
import './Dashboard.css';

// Section titles for the AdminShell topbar (same shell, user brand).
const USER_TITLE_MAP = {
  overview:  'Overview',
  nfts:      'My NFTs',
  streaming: 'Streaming Data',
  revenue:   'Growth',
  purchases: 'My Purchases',
  spots:     'Spots',
  support:   'Support',
};

// Public BSC RPC endpoints for read-only operations
const BSC_RPC_URLS = [
  'https://bsc-dataseed.bnbchain.org',
  'https://bsc-dataseed1.bnbchain.org',
  'https://bsc-dataseed2.bnbchain.org',
  'https://bsc-dataseed3.bnbchain.org'
];

// Get a Web3 instance for read-only operations (doesn't require wallet connection)
const getReadOnlyWeb3 = () => {
  try {
    return new Web3(BSC_RPC_URLS[0]);
  } catch (error) {
    console.error('Error creating read-only Web3 instance:', error);
    return null;
  }
};

const UserDashboard = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, login, isLoading: authLoading } = useAuth();
  const [signingIn, setSigningIn] = useState(false);
  const [signInError, setSignInError] = useState('');
  const { account, isConnected, web3 } = useWeb3();
  const [nfts, setNfts] = useState([]);
  const [, setStreamingData] = useState([]);
  const [, setStatistics] = useState(null);
  const [, setRevenueData] = useState(null);
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [unreadChat, setUnreadChat] = useState(0);
  const [nftBalance, setNftBalance] = useState(null);
  const [totalSupply, setTotalSupply] = useState(null);
  const [artistInfo, setArtistInfo] = useState([]);
  const [artistInfoMessage, setArtistInfoMessage] = useState('');
  const [linkInput, setLinkInput] = useState('');
  const [linkBusy, setLinkBusy] = useState(false);
  const [linkError, setLinkError] = useState('');
  const [artistRefreshing, setArtistRefreshing] = useState(false);
  const [growthStats, setGrowthStats] = useState(null);
  const [growthLoading, setGrowthLoading] = useState(false);
  const [growthServiceOpen, setGrowthServiceOpen] = useState(null);

  const fetchNFTBalance = useCallback(async () => {
    if (!account || !NFT_CONTRACT_ADDRESS) {
      setNftBalance(null);
      return;
    }

    // Use wallet web3 if available, otherwise use read-only Web3
    const web3Instance = web3 || getReadOnlyWeb3();
    if (!web3Instance) {
      setNftBalance(null);
      return;
    }

    try {
      const nftContract = new web3Instance.eth.Contract(NFT_ABI, NFT_CONTRACT_ADDRESS);
      const balance = await nftContract.methods.balanceOf(account.toLowerCase()).call();
      setNftBalance(parseInt(balance.toString(), 10));
    } catch (error) {
      console.error('Error fetching NFT balance:', error);
      setNftBalance(null);
    }
  }, [account, web3]);

  const fetchTotalSupply = useCallback(async () => {
    if (!NFT_CONTRACT_ADDRESS) {
      setTotalSupply(null);
      return;
    }

    // Use wallet web3 if available, otherwise use read-only Web3
    const web3Instance = web3 || getReadOnlyWeb3();
    if (!web3Instance) {
      setTotalSupply(null);
      return;
    }

    try {
      const nftContract = new web3Instance.eth.Contract(NFT_ABI, NFT_CONTRACT_ADDRESS);
      const supply = await nftContract.methods.totalSupply().call();
      setTotalSupply(parseInt(supply.toString(), 10));
    } catch (error) {
      console.error('Error fetching total supply:', error);
      setTotalSupply(null);
    }
  }, [web3]);

  // Fetch user's NFTs directly from blockchain
  const fetchUserNFTsFromChain = useCallback(async () => {
    if (!account || !NFT_CONTRACT_ADDRESS || !totalSupply || totalSupply === 0) {
      return [];
    }

    // Use wallet web3 if available, otherwise use read-only Web3
    const web3Instance = web3 || getReadOnlyWeb3();
    if (!web3Instance) {
      return [];
    }

    try {
      const nftContract = new web3Instance.eth.Contract(NFT_ABI, NFT_CONTRACT_ADDRESS);
      const userAddress = account.toLowerCase();
      
      // Detect tokenId indexing scheme (0-based vs 1-based)
      let startAtZero = false;
      try {
        const owner0 = await nftContract.methods.ownerOf(0).call();
        if (owner0 && owner0 !== '0x0000000000000000000000000000000000000000') {
          startAtZero = true;
        }
      } catch (_) {
        startAtZero = false;
      }
      
      // Check all tokens (0..totalSupply-1 OR 1..totalSupply) to find which ones the user owns
      const tokenIds = startAtZero
        ? Array.from({ length: totalSupply }, (_, i) => i)
        : Array.from({ length: totalSupply }, (_, i) => i + 1);
      const ownershipChecks = await Promise.all(
        tokenIds.map(async (tokenId) => {
          try {
            const owner = await nftContract.methods.ownerOf(tokenId).call();
            if (owner.toLowerCase() === userAddress) {
              return tokenId;
            }
            return null;
          } catch (error) {
            // Token might not exist or there's an error
            return null;
          }
        })
      );

      // Filter out null values to get owned token IDs
      const ownedTokenIds = ownershipChecks.filter(id => id !== null);

      // Fetch metadata for each owned NFT
      const nftsWithMetadata = await Promise.all(
        ownedTokenIds.map(async (tokenId) => {
          try {
            const metadataResponse = await api.get(`/nfts/${tokenId}/metadata`);
            const metadata = metadataResponse.data;
            
            return {
              tokenId,
              contractAddress: NFT_CONTRACT_ADDRESS,
              metadata: {
                name: metadata.name || `NFT #${tokenId}`,
                description: metadata.description || '',
                image: metadata.image || `https://phonestream.online/nfts/placeholder.png`,
                attributes: metadata.attributes || []
              }
            };
          } catch (error) {
            console.error(`Error fetching metadata for NFT #${tokenId}:`, error);
            return {
              tokenId,
              contractAddress: NFT_CONTRACT_ADDRESS,
              metadata: {
                name: `NFT #${tokenId}`,
                description: '',
                image: `https://phonestream.online/nfts/placeholder.png`,
                attributes: []
              }
            };
          }
        })
      );

      return nftsWithMetadata;
    } catch (error) {
      console.error('Error fetching user NFTs from chain:', error);
      return [];
    }
  }, [account, web3, totalSupply]);

  const fetchNFTMetadata = useCallback(async (nfts) => {
    // Fetch metadata for NFTs that don't have images
    const enrichedNFTs = await Promise.all(
      nfts.map(async (nft) => {
        // If NFT already has metadata with image, use it
        if (nft.metadata?.image) {
          return nft;
        }

        // Fetch metadata from API
        try {
          const metadataResponse = await api.get(`/nfts/${nft.tokenId}/metadata`);
          const metadata = metadataResponse.data;
          
          return {
            ...nft,
            metadata: {
              ...nft.metadata,
              name: metadata.name || nft.metadata?.name || `NFT #${nft.tokenId}`,
              description: metadata.description || nft.metadata?.description || '',
              image: metadata.image || nft.metadata?.image || `https://phonestream.online/nfts/placeholder.png`,
              attributes: metadata.attributes || nft.metadata?.attributes || []
            }
          };
        } catch (error) {
          console.error(`Error fetching metadata for NFT #${nft.tokenId}:`, error);
          // Return NFT with placeholder image if metadata fetch fails
          return {
            ...nft,
            metadata: {
              ...nft.metadata,
              image: `https://phonestream.online/nfts/placeholder.png`,
              name: nft.metadata?.name || `NFT #${nft.tokenId}`
            }
          };
        }
      })
    );

    return enrichedNFTs;
  }, []);

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Use Promise.allSettled to handle partial failures gracefully
      const results = await Promise.allSettled([
        api.get('/nfts/my-nfts').catch(() => ({ data: { nfts: [] } })),
        api.get('/streaming/my-data').catch(() => ({ data: { data: [] } })),
        api.get('/streaming/statistics').catch(() => ({ data: {} })),
        api.get('/revenue/statistics').catch(() => ({ data: {} })),
        api.get('/purchases/my-purchases').catch(() => ({ data: { purchases: [] } }))
      ]);

      // Extract data from results, handling both fulfilled and rejected promises
      const nftsRes = results[0].status === 'fulfilled' ? results[0].value : { data: { nfts: [] } };
      const streamingRes = results[1].status === 'fulfilled' ? results[1].value : { data: { data: [] } };
      const statsRes = results[2].status === 'fulfilled' ? results[2].value : { data: {} };
      const revenueRes = results[3].status === 'fulfilled' ? results[3].value : { data: {} };
      const purchasesRes = results[4].status === 'fulfilled' ? results[4].value : { data: { purchases: [] } };

      const rawNFTs = nftsRes.data.nfts || [];
      
      // Fetch metadata for NFTs from database to get images
      const enrichedNFTs = await fetchNFTMetadata(rawNFTs);
      
      // Also fetch NFTs directly from blockchain if user has wallet connected
      let chainNFTs = [];
      if (account && totalSupply && totalSupply > 0) {
        chainNFTs = await fetchUserNFTsFromChain();
      }
      
      // Merge database NFTs with chain NFTs, prioritizing chain data
      // Create a map of tokenId -> NFT to avoid duplicates
      const nftMap = new Map();
      
      // Add database NFTs first
      enrichedNFTs.forEach(nft => {
        if (nft.tokenId) {
          nftMap.set(nft.tokenId, nft);
        }
      });
      
      // Add/update with chain NFTs (chain data takes precedence)
      chainNFTs.forEach(nft => {
        nftMap.set(nft.tokenId, nft);
      });
      
      // Convert map to array
      const mergedNFTs = Array.from(nftMap.values());
      
      setNfts(mergedNFTs);
      setStreamingData(streamingRes.data.data || []);
      setStatistics(statsRes.data || {});
      setRevenueData(revenueRes.data || {});
      setPurchases(purchasesRes.data.purchases || []);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      // Set empty defaults for testing
      setNfts([]);
      setStreamingData([]);
      setStatistics({});
      setRevenueData({});
    } finally {
      setLoading(false);
    }
  }, [fetchNFTMetadata, fetchUserNFTsFromChain, account, totalSupply]);

  const autoFetchSpotifyData = useCallback(async () => {
    try {
      // Use test routes if not authenticated, regular routes if authenticated
      const walletAddress = account || localStorage.getItem('walletAddress');
      const basePath = (!isAuthenticated && walletAddress) ? '/test' : '';
      
      // First check if we have API keys configured
      const response = await api.get(`${basePath}/streaming/auto-fetch`);
      
      if (response.data.hasData && response.data.data) {
        setStreamingData(response.data.data || []);
        if (response.data.cached) {
          console.log('Using cached Spotify data');
        }
        fetchDashboardData();
      } else if (response.data.autoFetched) {
        setStreamingData(response.data.data || []);
        toast.success(`Auto-fetched ${response.data.data?.length || 0} entries from Spotify API`);
        fetchDashboardData();
      } else if (response.data.needsConfig) {
        console.log('ℹ️ Spotify API keys not configured in .env file');
      } else if (response.data.error) {
        console.log('ℹ️ Auto-fetch skipped:', response.data.message || response.data.error);
      } else {
        console.log('ℹ️ Auto-fetch response:', response.data);
      }
    } catch (error) {
      console.log('ℹ️ Auto-fetch unavailable:', error.response?.data?.message || error.message);
    }
  }, [account, isAuthenticated, fetchDashboardData]);

  // Recover from the connected-but-not-signed-in state (auto sign-in only runs
  // once; if its signature was dismissed/failed, this lets the user retry).
  const handleSignIn = useCallback(async () => {
    setSignInError('');
    setSigningIn(true);
    try {
      await login();
    } catch (e) {
      setSignInError(
        e?.response?.data?.message || e?.message || 'Sign-in failed. Please try again.'
      );
    } finally {
      setSigningIn(false);
    }
  }, [login]);

  const fetchArtistInfo = useCallback(async () => {
    try {
      const res = await api.get('/streaming/artist-info');
      setArtistInfo(res.data?.artists || []);
      setArtistInfoMessage(res.data?.message || '');
    } catch (e) {
      console.error('Artist info error:', e.message);
      setArtistInfoMessage(e.response?.data?.message || e.message || 'Failed to load artist data');
    }
  }, []);

  const refreshArtistInfo = useCallback(async () => {
    setArtistRefreshing(true);
    try {
      await fetchArtistInfo();
    } finally {
      setArtistRefreshing(false);
    }
  }, [fetchArtistInfo]);

  const linkArtist = useCallback(async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const value = linkInput.trim();
    if (!value) return;
    setLinkBusy(true);
    setLinkError('');
    try {
      await api.post('/streaming/my-artists', { link: value });
      setLinkInput('');
      await fetchArtistInfo();
    } catch (err) {
      setLinkError(err.response?.data?.message || err.message || 'Failed to link artist');
    } finally {
      setLinkBusy(false);
    }
  }, [linkInput, fetchArtistInfo]);

  const unlinkArtist = useCallback(async (artistId) => {
    if (!artistId) return;
    setLinkError('');
    try {
      await api.delete(`/streaming/my-artists/${artistId}`);
      await fetchArtistInfo();
    } catch (err) {
      setLinkError(err.response?.data?.message || err.message || 'Failed to remove artist');
    }
  }, [fetchArtistInfo]);

  // Growth tab: lifetime investment + campaign stats (caller-scoped, real data).
  const fetchGrowthStats = useCallback(async () => {
    setGrowthLoading(true);
    try {
      const res = await api.get('/user/analytics/range', { params: { range: 'all' } });
      setGrowthStats(res.data || null);
    } catch (e) {
      console.error('Growth stats error:', e.message);
      setGrowthStats(null);
    } finally {
      setGrowthLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'revenue' && !growthStats && !growthLoading) fetchGrowthStats();
  }, [activeTab, growthStats, growthLoading, fetchGrowthStats]);

  useEffect(() => {
    if (!isConnected && !(isAuthenticated && user?.isAdmin)) {
      navigate('/');
      return;
    }
    if (isConnected || (isAuthenticated && user?.isAdmin)) {
      fetchDashboardData();
      autoFetchSpotifyData();
      fetchArtistInfo();
    }
  }, [isConnected, isAuthenticated, user, fetchDashboardData, autoFetchSpotifyData, fetchArtistInfo, navigate]);

  // Fetch NFT balance from smart contract when wallet connects or account changes
  useEffect(() => {
    if (account) {
      fetchNFTBalance();
    } else {
      setNftBalance(null);
    }
  }, [account, fetchNFTBalance]);

  // Fetch total supply (total rigs in network) when web3 is available or account changes
  useEffect(() => {
    fetchTotalSupply();
  }, [fetchTotalSupply]);

  // Fetch user's NFTs from blockchain when account or totalSupply changes
  useEffect(() => {
    if (account && totalSupply && totalSupply > 0 && !loading) {
      const loadChainNFTs = async () => {
        try {
          const chainNFTs = await fetchUserNFTsFromChain();
          if (chainNFTs.length > 0) {
            // Merge with existing NFTs (preserve database data, add chain data)
            setNfts(prevNFTs => {
              const nftMap = new Map();
              // Add existing NFTs first
              prevNFTs.forEach(nft => {
                if (nft.tokenId) {
                  nftMap.set(nft.tokenId, nft);
                }
              });
              // Add/update with chain NFTs (chain data takes precedence for images/metadata)
              chainNFTs.forEach(nft => {
                nftMap.set(nft.tokenId, nft);
              });
              return Array.from(nftMap.values());
            });
          }
        } catch (error) {
          console.error('Error loading chain NFTs:', error);
        }
      };
      loadChainNFTs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account, totalSupply]); // Only depend on account and totalSupply to avoid loops


  const handleFetchSpotifyData = async () => {
    try {
      setLoading(true);
      
      // Use test routes if not authenticated, regular routes if authenticated
      const walletAddress = account || localStorage.getItem('walletAddress');
      const basePath = (!isAuthenticated && walletAddress) ? '/test' : '';
      
      // For test mode, trigger auto-fetch which will fetch data
      if (basePath === '/test') {
        const response = await api.get(`${basePath}/streaming/auto-fetch`);
        
        if (response.data.autoFetched && response.data.data) {
          setStreamingData(response.data.data || []);
          toast.success(`✨ Fetched ${response.data.data.length} entries from Spotify API!`);
          await fetchDashboardData();
        } else if (response.data.hasData && response.data.data) {
          setStreamingData(response.data.data || []);
          toast.info('Using cached data');
        } else {
          toast.error(response.data.message || 'Could not fetch data');
        }
      } else {
        // Authenticated mode - use regular endpoint
        const response = await api.post('/spotify/fetch-real-data', {
          timeRange: { days: 30 }
        });
        
        toast.success(response.data.message || 'Real Spotify data fetched successfully!');
        
        // Refresh dashboard data
        await fetchDashboardData();
        
        // Also refresh streaming data
        const streamingResponse = await api.get('/streaming/my-data');
        setStreamingData(streamingResponse.data.data || []);
      }
    } catch (error) {
      console.error('Fetch Spotify data error:', error);
      toast.error(error.response?.data?.message || 'Failed to fetch Spotify data. Make sure SPOTIFY_CLIENT_ID and SPOTIFY_CLIENT_SECRET are set in .env');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="dashboard-container">
        <Header />
        <div className="loading-container">
          <div>Loading dashboard...</div>
        </div>
      </div>
    );
  }

  // Sidebar nav config — computed here so we can fold in live state
  // (NFT count for the My NFTs label, purchase count, unread chat badge,
  // and gating the Spots tab on NFT ownership exactly like the old strip).
  const userNavGroups = [
    { label: 'Insights', items: [
      { key: 'overview', label: 'Overview', icon: '◴' },
    ]},
    { label: 'Music', items: [
      { key: 'nfts',
        label: `My NFTs${nftBalance !== null ? ` (${nftBalance})` : nfts.length ? ` (${nfts.length})` : ''}`,
        icon: '⌬' },
      { key: 'streaming', label: 'Streaming Data', icon: '⏵' },
      { key: 'revenue',   label: 'Growth',          icon: '↗' },
    ]},
    { label: 'Commerce', items: [
      { key: 'purchases',
        label: `My Purchases${purchases.length ? ` (${purchases.length})` : ''}`,
        icon: '▤' },
      ...(nftBalance > 0
        ? [{ key: 'spots', label: 'Spots', icon: '✦' }]
        : []),
    ]},
    { label: 'Help', items: [
      { key: 'support', label: 'Support', icon: '✉',
        badge: unreadChat > 0 ? unreadChat : null },
    ]},
  ];

  return (
    <div className="dashboard-container">
      <Header />
      
      <div className="dashboard-content">
        <AdminShell
          groups={userNavGroups}
          activeKey={activeTab}
          onChange={(k) => { setActiveTab(k); if (k === 'support') setUnreadChat(0); }}
          title={USER_TITLE_MAP[activeTab] || 'Dashboard'}
          brandText="My Dashboard"
          brandMark="◆"
        >
        <div className="dashboard-body">
          {isConnected && !isAuthenticated ? (
            <div className="dash-signin">
              <div className="dash-signin-card">
                <div className="dash-signin-mark">🔐</div>
                <h3>Sign in to your dashboard</h3>
                <p>
                  Your wallet <strong>{account ? `${account.slice(0, 6)}…${account.slice(-4)}` : ''}</strong> is connected.
                  Approve one quick signature to load your NFTs, streaming data, growth and orders.
                  It’s free and never moves funds.
                </p>
                <button
                  className="btn btn-primary dash-signin-btn"
                  onClick={handleSignIn}
                  disabled={signingIn || authLoading}
                >
                  {signingIn || authLoading ? 'Waiting for signature…' : 'Sign in with wallet'}
                </button>
                {signInError && <p className="dash-signin-error">{signInError}</p>}
              </div>
            </div>
          ) : (
          <>
          {activeTab === 'overview' && (
            <UserAnalyticsOverview onTabChange={(k) => setActiveTab(k)} />
          )}

          {activeTab === 'nfts' && (
            <Card noPad><div className="nfts-tab">
              <button
                className="btn btn-primary"
                onClick={handleFetchSpotifyData}
                style={{ marginBottom: '1rem' }}
              >
                Fetch Spotify Data
              </button>
              <div className="nfts-grid">
                {nfts.length === 0 ? (
                  <div className="empty-state">
                    <h3>No NFTs Found</h3>
                    <p>You don't have any NFTs yet. Here's how to get started:</p>
                    <ul>
                      <li>Mint an NFT from the landing page</li>
                      <li>Or verify ownership of an existing NFT</li>
                      <li>Once you have NFTs, they'll appear here</li>
                    </ul>
                  </div>
                ) : (
                  nfts.map((nft) => (
                    <div key={nft._id || `${nft.contractAddress || 'nft'}-${nft.tokenId}`} className="nft-card">
                      <div className="nft-image">
                        <img 
                          src={nft.metadata?.image || `https://phonestream.online/nfts/placeholder.png`} 
                          alt={nft.metadata?.name || `NFT #${nft.tokenId}`}
                          onError={(e) => {
                            // Fallback to placeholder if image fails to load
                            e.target.src = 'https://phonestream.online/nfts/placeholder.png';
                          }}
                        />
                      </div>
                      <div className="nft-info">
                        <h4>{nft.metadata?.name || `NFT #${nft.tokenId}`}</h4>
                        <p>Token ID: {nft.tokenId}</p>
                        <p>Streams: {nft.streamingData?.totalStreams || 0}</p>
                        <p>Revenue: ${(nft.streamingData?.totalRevenue || 0).toFixed(2)}</p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div></Card>
          )}

          {activeTab === 'streaming' && (
            <Card noPad><div className="streaming-tab">
              {/* Metrics summary strip */}
              {artistInfo.length > 0 && (
                <div className="artist-metrics-strip">
                  <div className="ams-tile">
                    <span className="ams-val">{artistInfo.length}</span>
                    <span className="ams-lbl">Linked Artist{artistInfo.length !== 1 ? 's' : ''}</span>
                  </div>
                  <div className="ams-tile">
                    <span className="ams-val">
                      {artistInfo.reduce((s, a) => s + (a.followers || 0), 0).toLocaleString()}
                    </span>
                    <span className="ams-lbl">Total Followers</span>
                  </div>
                  <div className="ams-tile">
                    <span className="ams-val">
                      {Math.round(
                        artistInfo.reduce((s, a) => s + (a.popularity || 0), 0) /
                          Math.max(1, artistInfo.length)
                      )}/100
                    </span>
                    <span className="ams-lbl">Avg Popularity</span>
                  </div>
                  <div className="ams-tile">
                    <span className="ams-val">
                      {artistInfo.reduce((s, a) => s + ((a.topTracks || []).length), 0)}
                    </span>
                    <span className="ams-lbl">Tracks Tracked</span>
                  </div>
                  <button
                    className="ams-refresh"
                    onClick={refreshArtistInfo}
                    disabled={artistRefreshing}
                    title="Refresh live Spotify metrics"
                  >
                    {artistRefreshing ? 'Refreshing…' : '↻ Refresh'}
                  </button>
                </div>
              )}

              {/* Self-service link form — NFT holders only */}
              {nftBalance > 0 ? (
                <form className="artist-link-form" onSubmit={linkArtist}>
                  <label className="alf-label">Link a Spotify artist</label>
                  <div className="alf-row">
                    <input
                      type="text"
                      className="alf-input"
                      placeholder="Paste a Spotify artist link (open.spotify.com/artist/…)"
                      value={linkInput}
                      onChange={(e) => setLinkInput(e.target.value)}
                      disabled={linkBusy}
                    />
                    <button type="submit" className="btn btn-primary alf-btn" disabled={linkBusy || !linkInput.trim()}>
                      {linkBusy ? 'Linking…' : 'Link Artist'}
                    </button>
                  </div>
                  <p className="alf-hint">
                    Open your artist on Spotify → Share → Copy link to artist, then paste it here. Metrics update live.
                  </p>
                  {linkError && <p className="alf-error">{linkError}</p>}
                </form>
              ) : artistInfo.length === 0 && nftBalance === 0 ? (
                <div className="artist-link-form alf-locked">
                  <p>🔒 Linking your own artist is a perk for NFT holders. Get a PhoneStream NFT to manage your artist profile here.</p>
                </div>
              ) : null}

              {artistInfo.length > 0 ? (
                <div className="artist-info-section">
                  {artistInfo.map(artist => (
                    <div key={artist.id} className="artist-card">
                      {nftBalance > 0 && (
                        <button
                          className="artist-unlink"
                          onClick={() => unlinkArtist(artist.id)}
                          title="Unlink this artist"
                        >
                          ✕ Unlink
                        </button>
                      )}
                      <div className="artist-header">
                        {artist.image && (
                          <img src={artist.image} alt={artist.name} className="artist-avatar" />
                        )}
                        <div className="artist-meta">
                          <h3>
                            {artist.name}
                            {artist.spotifyUrl && (
                              <a href={artist.spotifyUrl} target="_blank" rel="noopener noreferrer" className="spotify-link" title="Open on Spotify">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="#1DB954"><path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/></svg>
                              </a>
                            )}
                          </h3>
                          <div className="artist-stats-row">
                            <span className="artist-stat">
                              <strong>{(artist.followers || 0).toLocaleString()}</strong> followers
                            </span>
                            <span className="artist-stat">
                              Popularity: <strong>{artist.popularity}</strong>/100
                            </span>
                          </div>
                          {artist.genres.length > 0 && (
                            <div className="artist-genres">
                              {artist.genres.slice(0, 4).map(g => (
                                <span key={g} className="genre-tag">{g}</span>
                              ))}
                            </div>
                          )}
                          {artist.spotifyUrl && (
                            <a href={artist.spotifyUrl} target="_blank" rel="noopener noreferrer" className="artist-spotify-cta">
                              <svg width="15" height="15" viewBox="0 0 24 24" fill="#0b0b0b"><path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/></svg>
                              Open full profile on Spotify
                            </a>
                          )}
                          <p className="artist-note">
                            Spotify’s public API doesn’t expose “monthly listeners” — open the full profile to see them. Followers &amp; popularity above update live.
                          </p>
                        </div>
                      </div>

                      {/* Top Tracks (OAuth) or Releases (fallback) */}
                      {artist.topTracks && artist.topTracks.length > 0 ? (
                        <div className="top-tracks">
                          <h4>TOP TRACKS</h4>
                          <div className="tracks-list">
                            {artist.topTracks.map(t => (
                              <div key={t.id} className="track-row">
                                <span className="track-rank">#{t.rank}</span>
                                {t.image && <img src={t.image} alt="" className="track-album-art" />}
                                <div className="track-info">
                                  <span className="track-name">
                                    {t.spotifyUrl ? (
                                      <a href={t.spotifyUrl} target="_blank" rel="noopener noreferrer">{t.name}</a>
                                    ) : t.name}
                                  </span>
                                  <span className="track-album">{t.album}</span>
                                </div>
                                <span className="track-popularity">{t.popularity}</span>
                                <span className="track-duration">{Math.floor(t.durationMs/60000)}:{String(Math.floor((t.durationMs%60000)/1000)).padStart(2,'0')}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : artist.releases && artist.releases.length > 0 ? (
                        <div className="top-tracks">
                          <h4>Releases</h4>
                          <div className="tracks-list">
                            {artist.releases.map((r, i) => {
                              const href = r.spotifyUrl || artist.spotifyUrl || null;
                              return (
                                <a
                                  key={r.id}
                                  className={`track-row${href ? ' track-row-link' : ''}`}
                                  href={href || undefined}
                                  target={href ? '_blank' : undefined}
                                  rel={href ? 'noopener noreferrer' : undefined}
                                  title={href ? `Open “${r.name}” on Spotify` : undefined}
                                >
                                  <span className="track-rank">#{i + 1}</span>
                                  {r.image && <img src={r.image} alt="" className="track-album-art" />}
                                  <div className="track-info">
                                    <span className="track-name">
                                      {r.name}
                                      <span className="explicit-badge" style={{ background: '#444', marginLeft: 6 }}>{r.type}</span>
                                    </span>
                                    <span className="track-album">{r.releaseDate} · {r.totalTracks} track{r.totalTracks !== 1 ? 's' : ''}</span>
                                  </div>
                                  {href && <span className="track-open" aria-hidden="true">↗</span>}
                                </a>
                              );
                            })}
                          </div>
                        </div>
                      ) : (
                        <div className="top-tracks-empty">
                          <p>Detailed track data isn’t available right now.</p>
                          {artist.spotifyUrl && (
                            <a href={artist.spotifyUrl} target="_blank" rel="noopener noreferrer" className="artist-spotify-cta sm">
                              Open artist on Spotify ↗
                            </a>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-state">
                  <h3>No Artist Data Yet</h3>
                  <p>
                    {artistInfoMessage
                      ? artistInfoMessage
                      : nftBalance > 0
                        ? 'Link your Spotify artist above to see live followers, popularity, top tracks and releases.'
                        : 'Spotify artist data will appear here once an artist has been linked to your wallet.'}
                  </p>
                </div>
              )}
            </div></Card>
          )}

          {activeTab === 'revenue' && (() => {
            const gk = growthStats?.kpis || {};
            const invested = Number(gk.spend_usd?.value || 0);
            const storeSpend = Number(gk.store_spend_usd?.value || 0);
            const totalCampaigns = Number(gk.orders?.value || 0);
            const sb = growthStats?.status_breakdown || {};
            const activeCampaigns = ['Pending', 'In progress', 'Processing'].reduce((s, k) => s + Number(sb[k] || 0), 0);
            const completedCampaigns = Number(sb.Completed || 0);
            const topServices = growthStats?.top_services || [];
            return (
            <Card noPad><div className="growth-tab">
              <div className="growth-intro">
                <h3>Your Growth</h3>
                <p>The services you buy to grow <strong>your own</strong> profiles, plays and engagement — your marketing investment, tracked over time.</p>
              </div>

              {/* ── Your investment (for the user's OWN career) ──
                  RULE: KPI tiles are ALWAYS clickable — each one drills
                  into the underlying records the metric came from. */}
              <div className="growth-tiles">
                <button
                  type="button"
                  className="growth-tile growth-tile--clickable"
                  onClick={() => navigate('/store')}
                  title="See your SMM orders & invest more"
                >
                  <span className="growth-tile-lbl">Invested in services</span>
                  <span className="growth-tile-val">${invested.toFixed(2)}</span>
                  <span className="growth-tile-sub">across {totalCampaigns} order{totalCampaigns === 1 ? '' : 's'} →</span>
                </button>
                <button
                  type="button"
                  className="growth-tile growth-tile--clickable"
                  onClick={() => navigate('/store')}
                  title="See active SMM campaigns"
                >
                  <span className="growth-tile-lbl">Active campaigns</span>
                  <span className="growth-tile-val">{activeCampaigns}</span>
                  <span className="growth-tile-sub">{completedCampaigns} completed →</span>
                </button>
                {storeSpend > 0 && (
                  <button
                    type="button"
                    className="growth-tile growth-tile--clickable"
                    onClick={() => setActiveTab('purchases')}
                    title="See your store purchases"
                  >
                    <span className="growth-tile-lbl">Store purchases</span>
                    <span className="growth-tile-val">${storeSpend.toFixed(2)}</span>
                    <span className="growth-tile-sub">products bought →</span>
                  </button>
                )}
              </div>

              {growthLoading && !growthStats && (
                <p className="growth-muted">Loading your growth data…</p>
              )}

              {/* Where you invest — click a service to see your orders for it */}
              {topServices.length > 0 && (
                <div className="growth-section">
                  <h4>Where you invest</h4>
                  <div className="growth-services">
                    {topServices.map((s, i) => (
                      <button
                        key={`${s.service}-${i}`}
                        type="button"
                        className="growth-service-row growth-service-row--clickable"
                        onClick={() => setGrowthServiceOpen(s.service)}
                        title={`See your orders for “${s.service}”`}
                      >
                        <span className="growth-service-name" title={s.service}>{s.service}</span>
                        <span className="growth-service-meta">{s.orders} order{s.orders === 1 ? '' : 's'}</span>
                        <span className="growth-service-spend">${Number(s.spend || 0).toFixed(2)}</span>
                        <span className="growth-service-go" aria-hidden="true">→</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* ── The artist the NFT represents — only meaningful for NFT holders ── */}
              {nftBalance > 0 && artistInfo.length > 0 && (
                <div className="growth-section">
                  <h4>The artist your NFT represents</h4>
                  <p className="growth-note">
                    The Spotify artist your NFT is tied to — shown so you can follow how they’re doing.
                    This is <strong>separate</strong> from the services above: those promote your own profiles, not this artist.
                  </p>
                  <div className="growth-artist-grid">
                    {artistInfo.map((a) => (
                      <div key={a.id} className="growth-artist">
                        {a.image && <img src={a.image} alt={a.name} className="growth-artist-img" />}
                        <div className="growth-artist-body">
                          <span className="growth-artist-name">{a.name}</span>
                          <span className="growth-artist-stats">
                            <strong>{(a.followers || 0).toLocaleString()}</strong> followers · pop <strong>{a.popularity || 0}</strong>
                          </span>
                          {a.spotifyUrl && (
                            <a href={a.spotifyUrl} target="_blank" rel="noopener noreferrer" className="growth-artist-link">
                              Open on Spotify ↗
                            </a>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  <button className="btn btn-secondary growth-cta" onClick={() => setActiveTab('streaming')}>
                    View full streaming data →
                  </button>
                </div>
              )}

              {/* Empty state: nothing invested yet */}
              {!growthLoading && totalCampaigns === 0 && (
                <div className="empty-state">
                  <h3>Start growing</h3>
                  <p>Use our services to grow your own followers, plays and engagement — your investment and results will show up here.</p>
                  <a href="/store" className="btn btn-primary" style={{ marginTop: '1rem', display: 'inline-block', textDecoration: 'none' }}>
                    Browse services
                  </a>
                </div>
              )}

              <ServiceOrdersModal service={growthServiceOpen} onClose={() => setGrowthServiceOpen(null)} />
            </div></Card>
            );
          })()}

          {activeTab === 'purchases' && (
            <Card noPad><div className="purchases-tab">
              <h3>My Purchases</h3>
              {purchases.length === 0 ? (
                <div className="empty-state">
                  <h3>No Purchases Yet</h3>
                  <p>You haven't purchased any products yet. Visit the store to browse available music albums and products!</p>
                  <a href="/store" className="btn btn-primary" style={{ marginTop: '1rem', display: 'inline-block', textDecoration: 'none' }}>
                    Visit Store
                  </a>
                </div>
              ) : (
                <div className="purchases-list">
                  {purchases.map((purchase) => (
                    <div key={purchase._id} className="purchase-card">
                      <div className="purchase-header">
                        <div>
                          <h4>{purchase.productTitle || 'Product'}</h4>
                          <p className="purchase-date">
                            Purchased: {new Date(purchase.purchasedAt).toLocaleDateString()}
                          </p>
                          <p className="purchase-price">
                            {purchase.amount} {purchase.paymentCurrency}
                          </p>
                          {purchase.transactionHash && (
                            <a
                              href={`https://bscscan.com/tx/${purchase.transactionHash}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="transaction-link"
                            >
                              View Transaction ↗
                            </a>
                          )}
                        </div>
                      </div>
                      
                      {purchase.product && purchase.product.files && purchase.product.files.length > 0 && (
                        <div className="purchase-files">
                          <h5>Download Files:</h5>
                          <div className="files-list">
                            {purchase.product.files.map((file, index) => (
                              <div key={file._id || index} className="file-item">
                                <div className="file-info">
                                  <span className="file-name">{file.originalName || file.filename}</span>
                                  <span className="file-size">
                                    {file.fileSize ? `${(file.fileSize / (1024 * 1024)).toFixed(2)} MB` : ''}
                                  </span>
                                </div>
                                <a
                                  href={`/api/products/${purchase.product._id}/download/${file._id}`}
                                  className="btn btn-small btn-primary"
                                  onClick={async () => {
                                    // Record download
                                    try {
                                      await api.post(`/purchases/${purchase._id}/download`);
                                    } catch (error) {
                                      console.error('Error recording download:', error);
                                    }
                                  }}
                                  download
                                >
                                  Download
                                </a>
                              </div>
                            ))}
                          </div>
                          {purchase.downloadCount > 0 && (
                            <p className="download-count">
                              Downloaded {purchase.downloadCount} {purchase.downloadCount === 1 ? 'time' : 'times'}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div></Card>
          )}
          {/* Sub-panels wrapped in <Card noPad> for unified brand chrome.
              `noPad` lets each sub-component keep its own internal padding. */}
          {activeTab === 'spots' && nftBalance > 0 && (
            <Card noPad><SpotsTab /></Card>
          )}
          {activeTab === 'support' && (
            <Card noPad><UserChat onUnread={setUnreadChat} /></Card>
          )}
          </>
          )}
        </div>
        </AdminShell>
      </div>
      <Footer />
      {/* PHONE — native AI agent. Phase 3: user persona. */}
      <PhoneAIChat persona="user" />
    </div>
  );
};

export default UserDashboard;
